// Signup-source capture — pure, React-free so it runs at app startup
// (main.tsx) before React mounts and for LOGGED-OUT visitors.
//
// A partner's invite link (https://trybeup.com/?partner=CODE) is opened by
// someone who does not have an account yet. By the time the authenticated app
// mounts, the URL may already have been replaced by the router — so the code
// has to be grabbed at the earliest possible moment and stashed somewhere that
// survives the logged-out → signup → authenticated round-trip.
//
// This generalises `coachReferralCapture.ts` rather than replacing it: that
// module still owns `?coach=` for the coach rev-share binding, and this one
// records the SAME visit into the single durable column the Founding 100 roster
// is counted from. Both can run; the server resolves whichever code arrives.
//
// First touch wins. Whoever sent a man first is who sent him — a later link
// does not re-attribute a visit that is already carrying a code.

import { getStoredUtm } from "@/lib/utmCapture";

export const SIGNUP_SOURCE_STORAGE_KEY = "trybeup_signup_source";
// LEGACY, and the reason this file changed on 2026-08-25. This key used to mean
// "this device has bound a source, stop asking" — a DEVICE-wide latch on an
// ACCOUNT-scoped fact. Once set it was never cleared, so the device could never
// attribute another signup, ever. On the founder's phone it had been set by some
// earlier session, and the result was that two real signups through a live
// partner code recorded no source at all: `POST /api/attribution/bind` was never
// sent once, so attribution, the beta-tester entitlement and the cohort config —
// all three of which the server applies inside that one call — silently did not
// happen.
//
// It is also the outreach flow's own shape: /demo signs a leader into the demo
// account, which mounts the app and (before this fix) latched the flag; the same
// leader signing up for real minutes later would have been unattributable.
//
// Read nowhere now. Purged on startup so devices already carrying it heal.
const LEGACY_SIGNUP_SOURCE_BOUND_KEY = "trybeup_signup_source_bound";

// The query params that can carry a code, in priority order. `partner` is the
// First 100 code; `coach` and `ref` are the two that already existed, kept so
// links printed or sent before this shipped keep attributing.
//
// `tester` is the SAME partner code on a param that reads like what it is — the
// beta-tester link (T8). It is deliberately not a separate mechanism: if the
// offer link and the attribution link could come apart, a man could redeem a
// year of free Premium and arrive unattributed, and an unattributable signup is
// one that can't go on the invoice. One code, two spellings, one resolver.
const CODE_PARAMS = ["partner", "tester", "coach", "ref"] as const;

/**
 * Reads a source code from the current URL and stashes it. Idempotent and safe
 * to call anywhere, any number of times.
 *
 * Unlike the coach capture, this does NOT strip the param from the URL — the
 * coach module already does that for `?coach=`, and stripping `?partner=` here
 * would race PostHog's pageview, which reads the query string on init.
 */
export function captureSignupSourceFromUrl(): void {
  try {
    purgeLegacyBoundFlag();
    const params = new URLSearchParams(window.location.search);
    for (const key of CODE_PARAMS) {
      const raw = params.get(key);
      if (!raw || !raw.trim()) continue;
      // First touch wins — never overwrite a code we already hold.
      if (localStorage.getItem(SIGNUP_SOURCE_STORAGE_KEY)) return;
      localStorage.setItem(SIGNUP_SOURCE_STORAGE_KEY, raw.trim().toUpperCase().slice(0, 64));
      return;
    }
  } catch {
    /* ignore — attribution is best-effort on the client, authoritative on the server */
  }
}

/** The stashed code, or null when the visit carried none. */
export function getStoredSignupSource(): string | null {
  try {
    return localStorage.getItem(SIGNUP_SOURCE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Drop the stashed code once the server has told us it actually recorded a
 * source for this account.
 *
 * Gated on the server's `recorded` flag, not on "the request came back 200":
 * a repeat call answers 200 with `recorded: false`, and clearing on that would
 * be the old device-latch bug wearing a different hat.
 *
 * Why clear at all, when the server is set-once and a stale code is harmless to
 * the account that already has one: a SECOND account signing up on the same
 * device would otherwise inherit the first one's code and land on a partner's
 * roster nobody sent them to. First touch wins within a signup, not forever.
 */
export function clearStoredSignupSource(): void {
  try {
    localStorage.removeItem(SIGNUP_SOURCE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Remove the old device-wide latch (see the constant above) so a phone that
 *  already carries it can attribute again. Safe to call on every startup. */
export function purgeLegacyBoundFlag(): void {
  try {
    localStorage.removeItem(LEGACY_SIGNUP_SOURCE_BOUND_KEY);
  } catch {
    /* ignore */
  }
}

/** The payload for POST /api/attribution/bind. A visit with no code still
 *  sends — that is how "nobody sent him" gets recorded as a fact rather than
 *  left blank. */
export function signupSourcePayload(): { code: string | null; utm: Record<string, string> | null } {
  const utm = getStoredUtm();
  let cleaned: Record<string, string> | null = null;
  if (utm) {
    const { capturedAt: _capturedAt, ...rest } = utm;
    cleaned = Object.keys(rest).length ? (rest as Record<string, string>) : null;
  }
  return { code: getStoredSignupSource(), utm: cleaned };
}
